import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { SearchStatus, TravelTypes } from '../../../../constants';
import AddUpdateBrochure from '..';
import getBrochureTripId from '../../../../helpers/getBrochureTripId';
import useHandleHotelAddToBrochure from '../../../../hooks/useHandleHotelAddToBrochure';
import arePassengerCountsEqual from '../../../../helpers/checkArePassengerCountsEqual';
import withSpinner from '../../../../HOCs/withSpinner';
import { setProductsCodesAddedToBrochure } from '../../../../store/slices/brochure';

const HotelAddUpdateToBrochure = ({
  tripId, hotelItinerary, className, product, getImprovedRateDescription, tripRoomSearchInput,
  productsCodesAddingToBrochure,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { brochure } = useSelector((state) => state.brochure);
  const [isSelected, setIsSelected] = useState(false);
  const { getRoomDetailsStatus = {} } = useSelector((state) => state.hotel);
  const { isViewingBrochure } = useSelector((state) => state.brochure);
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const {
    staterooms: stateroomsObj,
  } = useSelector((store) => store.tripSearch.searchObj);

  const { travelAgency } = useSelector((state) => state.user);
  const brochureDisplayName = travelAgency?.settings?.brochureSettings?.DisplayName;

  const productsCodesAddedToBrochure = useSelector((store) => store?.brochure
    ?.productsCodesAddedToBrochure ?? []);

  const id = useMemo(() => (
    getBrochureTripId(tripId, brochure, hotelItinerary,
      tripRoomSearchInput || stateroomsObj, TravelTypes.hotel)),
  [brochure, hotelItinerary, stateroomsObj, tripId, tripRoomSearchInput]);

  const findHotelItinerary = useCallback(
    (tripSegments, hotelKey, staterooms) => _.find(tripSegments,
      (ts) => ts.itinerary.hotelKey === hotelKey
      && ts.itinerary?.startDateTime === hotelItinerary?.startDateTime
      && ts.itinerary?.endDateTime === hotelItinerary?.endDateTime
      && arePassengerCountsEqual(staterooms, tripRoomSearchInput || stateroomsObj)),
    [hotelItinerary.endDateTime, hotelItinerary.startDateTime, stateroomsObj, tripRoomSearchInput],
  );

  const findHotelRoom = (products, productCode) => _.find(products,
    (i) => i.code === productCode);

  const findHotelItineraries = (trip) => _.filter(trip.tripSegments,
    (ts) => ts.itinerary.mode === TravelTypes.hotel);

  const currentBrochure = useSelector((state) => state.brochure.brochures[brochure && brochure.id]);
  const brochureOnBrochurePage = useSelector((state) => state.brochure.brochures[tripId]);

  const setSelectedTripSegment = useCallback((tripSegments, staterooms) => {
    if (tripSegments && tripSegments.length) {
      const matchedHotelTripSeg = findHotelItinerary(tripSegments,
        hotelItinerary.hotelKey, staterooms);
      if (matchedHotelTripSeg) {
        const matchedProduct = findHotelRoom(matchedHotelTripSeg.itinerary.hotel.products,
          product.code);
        if (matchedProduct) {
          setIsSelected(true);
        }
      }
    }
  }, [findHotelItinerary, hotelItinerary.hotelKey, product.code]);

  useEffect(() => {
    setIsSelected(false);

    const findAndSetTripSegments = (brochureData, tripIdentifier) => {
      if (!brochureData || !brochureData.trips || brochureData.trips.length === 0) {
        return;
      }

      const isMatchingHotelSegment = (segment, trip) => {
        const itinerary = segment?.itinerary;

        return itinerary?.mode === TravelTypes.hotel
        && itinerary?.hotelKey === hotelItinerary?.hotelKey
        && itinerary?.startDateTime === hotelItinerary?.startDateTime
        && itinerary?.endDateTime === hotelItinerary?.endDateTime
        && arePassengerCountsEqual(
          tripRoomSearchInput,
          trip?.roomSearchInput ?? trip?.staterooms,
        );
      };

      const isMatchingTrip = (trip) => trip.id === tripIdentifier
        && trip?.tripSegments?.some((segment) => isMatchingHotelSegment(segment, trip));

      const foundTrip = brochureData.trips.find(isMatchingTrip);

      if (foundTrip) {
        const tripSegments = findHotelItineraries(foundTrip);
        if (tripSegments) {
          setSelectedTripSegment(tripSegments, foundTrip?.staterooms
          ?? foundTrip?.roomSearchInput);
        }
      }
    };

    if (isBrochurePage && brochureOnBrochurePage && tripId) {
      findAndSetTripSegments(brochureOnBrochurePage, tripId);
      return;
    }

    if (isViewingBrochure && currentBrochure && brochure && brochure.id) {
      findAndSetTripSegments(currentBrochure, brochure.id);
      return;
    }

    if (brochure && brochure.trips && brochure.trips[id]) {
      const tripSegments = findHotelItineraries(brochure.trips[id]);
      if (tripSegments && tripSegments.length > 0) {
        setSelectedTripSegment(tripSegments, brochure?.trips?.[id]?.staterooms
          ?? brochure?.trips?.[id]?.roomSearchInput);
      }
    }
  }, [
    brochure,
    brochureOnBrochurePage,
    currentBrochure,
    hotelItinerary,
    isBrochurePage,
    isViewingBrochure,
    product,
    setSelectedTripSegment,
    tripId,
    id,
    stateroomsObj,
    tripRoomSearchInput,
  ]);
  const originalHandleAddToBrochureClick = useHandleHotelAddToBrochure({
    tripId,
    hotelItinerary,
    product,
    brochure,
    isViewingBrochure,
    isBrochurePage,
    currentBrochure,
    brochureOnBrochurePage,
    tripRoomSearchInput,
  });

  useEffect(() => {
    if (productsCodesAddedToBrochure.includes(product.code)) {
      originalHandleAddToBrochureClick();
      dispatch(setProductsCodesAddedToBrochure({ productCode: product.code }));
    }
  }, [dispatch, originalHandleAddToBrochureClick, product, productsCodesAddedToBrochure]);

  const handleAddToBrochureClick = () => {
    if (!isSelected) getImprovedRateDescription();
    if (product.additional_info || isSelected) originalHandleAddToBrochureClick();
  };

  const doShowAddingToBrochure = productsCodesAddingToBrochure.includes(product.code);
  if (doShowAddingToBrochure) return <div className="text-base text-theme cursor-pointer z-10 col-span-1 text-right inline-block">{withSpinner(<></>, true, `Adding to ${brochureDisplayName?.toLowerCase() || 'brochure'}...`, 'left-inline', 'xs')}</div>;
  if (getRoomDetailsStatus[product.code] === SearchStatus.SEARCHING) return <></>;

  return (
    <AddUpdateBrochure
      handleAddToBrochureClick={handleAddToBrochureClick}
      className={className}
      isSelected={isSelected}
    />
  );
};

HotelAddUpdateToBrochure.propTypes = {
  className: PropTypes.string,
  tripId: PropTypes.string.isRequired,
  hotelItinerary: PropTypes.shape().isRequired,
  product: PropTypes.shape().isRequired,
  getImprovedRateDescription: PropTypes.func,
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
  productsCodesAddingToBrochure: PropTypes.arrayOf(PropTypes.string),

};

HotelAddUpdateToBrochure.defaultProps = {
  className: '',
  getImprovedRateDescription: undefined,
  tripRoomSearchInput: undefined,
  productsCodesAddingToBrochure: [],
};
export default HotelAddUpdateToBrochure;
